/**
 * 统一的日志工具
 * 支持开发/生产环境控制，便于调试和问题排查
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * 日志级别
 */
export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

/**
 * 日志配置
 */
interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  prefix?: string;
}

const defaultConfig: LoggerConfig = {
  enabled: isDevelopment,
  level: LogLevel.INFO,
};

/**
 * 日志工具类
 */
class Logger {
  private config: LoggerConfig;
  private prefix: string;

  constructor(prefix: string = '', config?: Partial<LoggerConfig>) {
    this.prefix = prefix;
    this.config = { ...defaultConfig, ...config };
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;
    
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);
    
    return messageLevelIndex >= currentLevelIndex;
  }

  private formatMessage(level: LogLevel, message: string, ...args: any[]): void {
    if (!this.shouldLog(level)) return;

    const prefix = this.prefix ? `[${this.prefix}]` : '';
    const logMessage = `${prefix} ${message}`;

    switch (level) {
      case LogLevel.DEBUG:
        console.log(`🔍 ${logMessage}`, ...args);
        break;
      case LogLevel.INFO:
        console.log(`ℹ️ ${logMessage}`, ...args);
        break;
      case LogLevel.WARN:
        console.warn(`⚠️ ${logMessage}`, ...args);
        break;
      case LogLevel.ERROR:
        console.error(`❌ ${logMessage}`, ...args);
        break;
    }
  }

  debug(message: string, ...args: any[]): void {
    this.formatMessage(LogLevel.DEBUG, message, ...args);
  }

  info(message: string, ...args: any[]): void {
    this.formatMessage(LogLevel.INFO, message, ...args);
  }

  warn(message: string, ...args: any[]): void {
    this.formatMessage(LogLevel.WARN, message, ...args);
  }

  error(message: string, ...args: any[]): void {
    this.formatMessage(LogLevel.ERROR, message, ...args);
  }

  /**
   * 创建带前缀的日志实例
   */
  createChild(prefix: string): Logger {
    const fullPrefix = this.prefix ? `${this.prefix}:${prefix}` : prefix;
    return new Logger(fullPrefix, this.config);
  }
}

/**
 * 创建日志实例
 */
export function createLogger(prefix: string = '', config?: Partial<LoggerConfig>): Logger {
  return new Logger(prefix, config);
}

/**
 * 默认日志实例
 */
export const logger = createLogger('StoreRevenueMonitor');
